import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, QueryChips } from '@teals/ui-web';
import { useSendMessageMutation } from '../../store/api';
import { useWebSocketContext } from '../../providers/WebSocketProvider';
import type { ExchangeEvent } from '../../hooks/useWebSocket';
import { useVoiceChat } from '../../hooks/useVoiceChat';
import VoiceChatBar, { VoiceMicButton } from '../../components/VoiceChatBar';
import {
  attachmentToken,
  configsDiffer,
  dedupeAttachments,
  parseBuilderResponse,
  parseIntegrationDrop,
  toProposed,
  type AgentConfigSnapshot,
  type BuilderAttachment,
  type ChatMessage,
  type ProposedConfig,
} from './builderHelpers';

const BUILDER_AGENT = 'agent-builder';

const ChatShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 28rem;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  overflow: hidden;
`;

const ChatHeader = styled.div`
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const ChatTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
`;

const ChatSubtitle = styled.div`
  margin-top: 0.25rem;
  font-size: 0.8125rem;
  color: var(--text-muted);
  line-height: 1.4;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 16rem;
  max-height: 28rem;
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant' }>`
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 85%;
  padding: 0.65rem 0.85rem;
  border-radius: 12px;
  white-space: pre-wrap;
  font-size: 0.875rem;
  line-height: 1.45;
  background: ${({ $role }) =>
    $role === 'user' ? 'var(--accent-muted, #e0f2fe)' : 'var(--surface-2, #f8fafc)'};
  color: var(--text);
`;

const PendingBubble = styled.div`
  align-self: flex-start;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const Dropzone = styled.div<{ $over?: boolean }>`
  margin: 0 1rem 1rem;
  padding: 0.75rem;
  border: 1px dashed
    ${({ $over }) => ($over ? 'var(--accent)' : 'var(--border)')};
  border-radius: 10px;
  background: ${({ $over }) =>
    $over ? 'var(--accent-muted, #ecfeff)' : 'transparent'};
`;

const DropLabel = styled.div<{ $over?: boolean }>`
  font-size: 0.75rem;
  color: ${({ $over }) => ($over ? 'var(--accent)' : 'var(--text-muted)')};
  margin-bottom: 0.5rem;
`;

const AttachedRefs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
`;

const AttachedRef = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: var(--surface-2, #f1f5f9);
`;

const ChipRemove = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 0.1rem;
  color: var(--text-muted);
`;

const ChatInputRow = styled.form`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`;

const ChatInput = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 2.5rem;
  max-height: 8rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  font: inherit;
  background: var(--surface);
  color: var(--text);
`;

const ChipWrap = styled.div`
  margin-top: 0.75rem;
`;

const DiffCard = styled.div`
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  font-size: 0.875rem;
`;

const DiffActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const ErrorBanner = styled.div`
  margin-top: 0.75rem;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  background: #fef2f2;
  color: #991b1b;
  font-size: 0.8125rem;
`;

type CreateJobMark = 'conversational' | 'management' | 'task';

const CreateShell = styled(ChatShell)<{ $over?: boolean }>`
  min-height: 0;
  border-color: ${({ $over }) => ($over ? 'var(--accent)' : 'var(--border)')};
  box-shadow: ${({ $over }) =>
    $over ? 'var(--shadow-md)' : 'var(--shadow-sm)'};
`;

const Stage = styled.div`
  padding: 1.75rem 1.5rem 0.25rem;
  display: flex;
  flex-direction: column;
`;

const StageTitle = styled.h2`
  margin: 0;
  font-size: 1.375rem;
  font-weight: 600;
  letter-spacing: -0.03em;
  color: var(--text-primary, var(--text));
`;

const StageLead = styled.p`
  margin: 0.4rem 0 0;
  max-width: 34rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--text-secondary, var(--text));
`;

const JobList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1.35rem;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
`;

const JobRow = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  width: 100%;
  text-align: left;
  padding: 0.9rem 1rem;
  border: none;
  border-bottom: 1px solid var(--border);
  border-radius: 0;
  background: ${({ $selected }) =>
    $selected
      ? 'var(--accent-subtle)'
      : 'var(--bg-surface, var(--surface))'};
  color: inherit;
  font: inherit;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $selected }) =>
      $selected
        ? 'var(--accent-subtle)'
        : 'var(--bg-elevated, var(--surface-2, #f8fafc))'};
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

const AgentMark = styled.span<{ $kind: CreateJobMark }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0.05rem;
  background: ${({ $kind }) =>
    $kind === 'management'
      ? 'var(--accent-subtle)'
      : $kind === 'task'
        ? 'var(--warning-subtle)'
        : 'var(--bg-elevated, var(--surface-2))'};
  color: ${({ $kind }) =>
    $kind === 'management'
      ? 'var(--accent)'
      : $kind === 'task'
        ? 'var(--warning)'
        : 'var(--text-secondary, var(--text-muted))'};
`;

const JobCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
`;

const JobTitle = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--text-primary, var(--text));
`;

const JobDesc = styled.span`
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--text-muted);
`;

const CreateDropSlot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 1.25rem;
`;

const DropHint = styled.div`
  margin: 0;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  border: 1px dashed var(--accent);
  background: var(--accent-subtle, var(--accent-muted, #ecfeff));
  color: var(--accent-text, var(--accent));
  font-size: 0.8125rem;
  font-weight: 500;
`;

const CreateComposer = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.25rem 1.25rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const CreateInput = styled(ChatInput)`
  min-height: 4.5rem;

  @media (max-width: 768px) {
    flex-basis: 100%;
  }
`;

const ComposerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  button[data-testid='builder-voice-mic'] {
    border-radius: 8px;
  }

  @media (max-width: 768px) {
    margin-left: auto;
  }
`;

const SendButton = styled(Button)`
  && {
    height: 36px;
    padding: 0 0.9rem;
    border-radius: 8px;
    line-height: 1;
  }
`;

const VoiceSlot = styled.div`
  padding: 0 1.25rem 1rem;
`;

interface CreateJob {
  id: string;
  title: string;
  description: string;
  prompt: string;
  mark: CreateJobMark;
}

const CREATE_JOBS: CreateJob[] = [
  {
    id: 'knowledge',
    title: 'Answer from a knowledge base',
    description:
      'Customer questions, short replies, and an honest “I don’t know.”',
    prompt:
      'A conversational agent that answers customer questions from our FAQ. Keep replies short, and say clearly when it does not know.',
    mark: 'conversational',
  },
  {
    id: 'route',
    title: 'Route work to specialists',
    description:
      'Reads each request and sends it to the agent that should handle it.',
    prompt:
      'A management agent that reads each request and routes it to the right specialist.',
    mark: 'management',
  },
  {
    id: 'extract',
    title: 'Extract fields from a document',
    description: 'Reads an upload and returns the same fields every time.',
    prompt:
      'A task agent that reads an uploaded document and returns a fixed set of fields.',
    mark: 'task',
  },
];

function JobTypeMark({ kind }: { kind: CreateJobMark }) {
  if (kind === 'management') {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path
          d="M7 2L12 7L7 12L2 7Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (kind === 'task') {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path
          d="M3 4h8M3 7h8M3 10h5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="2" y="3" width="10" height="2.25" rx="1.125" fill="currentColor" />
      <rect x="2" y="8.5" width="7" height="2.25" rx="1.125" fill="currentColor" />
    </svg>
  );
}

export interface BuilderAcceptArgs {
  proposed: ProposedConfig;
  agentConfig: Record<string, unknown>;
  conversationId?: string;
  droppedRefs: BuilderAttachment[];
}

export interface BuilderChatProps {
  mode: 'create' | 'configure';
  greeting: string;
  subtitle: string;
  placeholder: string;
  seedChips: string[];
  promptContext: Record<string, unknown>;
  metadataSource: 'create' | 'configure';
  editsEnabled: boolean;
  inputDisabled?: boolean;
  currentConfig: AgentConfigSnapshot;
  fleetNames: string[];
  dataSourceNames: string[];
  appliedMessage?: string;
  onAccept: (args: BuilderAcceptArgs) => Promise<void>;
  onChipClick?: (chip: string) => boolean | void;
  onPendingChange?: (pending: ProposedConfig | null) => void;
  onContentChange?: (hasUserContent: boolean) => void;
  composerSeed?: { id: number; text: string } | null;
  attachSeed?: { id: number; refs: BuilderAttachment[] } | null;
}

const BuilderChat: React.FC<BuilderChatProps> = ({
  mode,
  greeting,
  subtitle,
  placeholder,
  seedChips,
  promptContext,
  metadataSource,
  editsEnabled,
  inputDisabled = false,
  currentConfig,
  fleetNames,
  dataSourceNames,
  appliedMessage,
  onAccept,
  onChipClick,
  onPendingChange,
  onContentChange,
  composerSeed = null,
  attachSeed = null,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    mode === 'create'
      ? []
      : [{ id: 'greeting', role: 'assistant', content: greeting }],
  );
  const [chatInput, setChatInput] = useState('');
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [attachedRefs, setAttachedRefs] = useState<BuilderAttachment[]>([]);
  const [sessionRefs, setSessionRefs] = useState<BuilderAttachment[]>([]);
  const [dropOver, setDropOver] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [pending, setPending] = useState<ProposedConfig | null>(null);
  const [pendingRaw, setPendingRaw] = useState<Record<string, unknown> | null>(
    null,
  );
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const greetedRef = useRef(false);

  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const { subscribe, unsubscribe, addListener, removeListener } =
    useWebSocketContext();
  const speakRef = useRef<(text: string) => void>(() => {});
  const handleVoiceTranscript = useCallback((text: string) => {
    setChatInput((prev) => (prev ? `${prev} ${text}` : text));
  }, []);
  const {
    voiceState,
    error: voiceError,
    startRecording,
    stopRecording,
    speak,
    stopSpeaking,
  } = useVoiceChat({ onTranscript: handleVoiceTranscript });
  speakRef.current = speak;

  const showPending = isSending || awaitingReply;
  const locked = inputDisabled || showPending;

  const configRef = useRef(currentConfig);
  const fleetRef = useRef(fleetNames);
  const dsNamesRef = useRef(dataSourceNames);
  configRef.current = currentConfig;
  fleetRef.current = fleetNames;
  dsNamesRef.current = dataSourceNames;

  useEffect(() => {
    if (mode === 'create') {
      greetedRef.current = true;
      return;
    }
    if (greetedRef.current) return;
    greetedRef.current = true;
    setMessages([{ id: 'greeting', role: 'assistant', content: greeting }]);
  }, [greeting, mode]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, awaitingReply]);

  useEffect(() => {
    if (!composerSeed?.text) return;
    const addition = composerSeed.text.trim();
    if (!addition) return;
    setChatInput((prev) => {
      if (!prev.trim()) return addition;
      if (prev.includes(addition)) return prev;
      return `${prev.trim()} ${addition}`;
    });
  }, [composerSeed]);

  useEffect(() => {
    if (!attachSeed?.refs?.length) return;
    setAttachedRefs((prev) => dedupeAttachments([...prev, ...attachSeed.refs]));
  }, [attachSeed]);

  useEffect(() => {
    if (!sessionId) return;
    subscribe(sessionId, BUILDER_AGENT);
    return () => unsubscribe();
  }, [sessionId, subscribe, unsubscribe]);

  useEffect(() => {
    onPendingChange?.(pending);
  }, [pending, onPendingChange]);

  useEffect(() => {
    const hasUser = messages.some((m) => m.role === 'user');
    onContentChange?.(hasUser);
  }, [messages, onContentChange]);

  const applyAssistantText = useCallback(
    (content: string) => {
      const parsed = parseBuilderResponse(content);
      const message =
        parsed.message ||
        (parsed.agentConfig
          ? 'Proposed configuration — review and Accept or Discard.'
          : content);
      setMessages((prev) => {
        if (prev.some((m) => m.content === message && m.role === 'assistant')) {
          return prev;
        }
        return [
          ...prev,
          { id: `a-${Date.now()}`, role: 'assistant', content: message },
        ];
      });
      void speakRef.current(message);
      if (parsed.suggestions?.length) {
        setSuggestions(parsed.suggestions);
      }
      if (parsed.agentConfig && editsEnabled) {
        const proposed = toProposed(parsed.agentConfig);
        const differs =
          mode === 'create'
            ? Boolean(
                proposed.agentName ||
                  proposed.systemPrompt ||
                  proposed.model,
              )
            : configsDiffer(
                configRef.current,
                fleetRef.current,
                dsNamesRef.current,
                proposed,
              );
        if (differs) {
          setPending(proposed);
          setPendingRaw(parsed.agentConfig);
        }
      }
    },
    [editsEnabled, mode],
  );

  useEffect(() => {
    const listener = (event: ExchangeEvent) => {
      if (
        (event.type === 'exchange.response.completed' ||
          event.type === 'conversation.result' ||
          event.type === 'exchange.completed') &&
        event.data?.response
      ) {
        const content = String(event.data.response);
        setAwaitingReply(false);
        applyAssistantText(content);
      }
      if (
        event.type === 'conversation.result' &&
        (event.data?.status === 'failed' || event.data?.error)
      ) {
        setAwaitingReply(false);
        setMessages((prev) => [
          ...prev,
          {
            id: `err-${Date.now()}`,
            role: 'assistant',
            content: String(
              event.data?.error ||
                event.data?.message ||
                'Configuration assistant failed.',
            ),
          },
        ]);
      }
    };
    addListener(listener);
    return () => removeListener(listener);
  }, [addListener, removeListener, applyAssistantText]);

  const displayChips = suggestions.length ? suggestions : seedChips;

  const sendQuery = async (query: string, refs?: BuilderAttachment[]) => {
    const attached = refs ?? attachedRefs;
    const text = query.trim();
    if ((!text && !attached.length) || locked) return;

    const refPrefix =
      attached.length > 0
        ? `${attached.map((ref) => attachmentToken(ref)).join(' ')}\n`
        : '';
    const fullQuery = `${refPrefix}${text}`.trim();

    setSessionRefs((prev) => dedupeAttachments([...prev, ...attached]));
    setChatInput('');
    setAttachedRefs([]);
    setActionError(null);
    setMessages((prev) => [
      ...prev,
      { id: `u-${Date.now()}`, role: 'user', content: fullQuery },
    ]);
    setAwaitingReply(true);
    try {
      const result = await sendMessage({
        query: fullQuery,
        agentName: BUILDER_AGENT,
        sessionId,
        metadata: {
          source: metadataSource,
          prompt_context: promptContext,
          attached_data_sources: attached
            .filter((ref) => ref.kind === 'data_source')
            .map((ref) => ref.name),
          attached_mcp_servers: attached
            .filter((ref) => ref.kind === 'mcp')
            .map((ref) => ref.name),
        },
      }).unwrap();
      if (result.sessionId) {
        setSessionId(result.sessionId);
        subscribe(result.sessionId, BUILDER_AGENT);
      }
      if (result.conversationId) {
        setConversationId(result.conversationId);
      }
      const replyText = (result.response || '').trim();
      const pendingAsync =
        result.isAsync === true ||
        result.status === 'processing' ||
        (!replyText && Boolean(result.conversationId));
      if (pendingAsync) return;
      setAwaitingReply(false);
      if (replyText) applyAssistantText(replyText);
    } catch (err: unknown) {
      setAwaitingReply(false);
      const status =
        err && typeof err === 'object' && 'status' in err
          ? Number((err as { status?: number }).status)
          : undefined;
      const message =
        status === 404
          ? 'Agent Builder is not available in this environment.'
          : 'Unable to reach the configuration assistant.';
      setActionError(message);
      setMessages((prev) => [
        ...prev,
        { id: `e-${Date.now()}`, role: 'assistant', content: message },
      ]);
    }
  };

  const handleChipSelect = (chip: string) => {
    if (locked) return;
    if (onChipClick?.(chip)) return;
    void sendQuery(chip);
  };

  const onDropRef = (ref: BuilderAttachment) => {
    setAttachedRefs((prev) => dedupeAttachments([...prev, ref]));
    if (!chatInput.trim()) {
      setChatInput(
        ref.kind === 'mcp'
          ? `Associate Custom MCP ${ref.name}.`
          : `Associate and use ${ref.name} in the system prompt.`,
      );
    }
  };

  const handleAccept = async () => {
    if (!pending || !pendingRaw || !editsEnabled) return;
    setSaving(true);
    setActionError(null);
    try {
      await onAccept({
        proposed: pending,
        agentConfig: pendingRaw,
        conversationId,
        droppedRefs: dedupeAttachments([...sessionRefs, ...attachedRefs]),
      });
      setPending(null);
      setPendingRaw(null);
      if (appliedMessage) {
        setMessages((prev) => [
          ...prev,
          {
            id: `ok-${Date.now()}`,
            role: 'assistant',
            content: appliedMessage,
          },
        ]);
      }
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to apply proposal',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setPending(null);
    setPendingRaw(null);
  };

  const fleetDiffLines = useMemo(() => {
    if (!pending?.fleet) return [];
    const current = new Set(fleetNames);
    const next = new Set(pending.fleet);
    const removed = fleetNames.filter((n) => !next.has(n));
    const added = pending.fleet.filter((n) => !current.has(n));
    return [...removed.map((n) => `− ${n}`), ...added.map((n) => `+ ${n}`)];
  }, [pending, fleetNames]);

  const chooseJob = (job: CreateJob) => {
    setSelectedJob(job.id);
    setChatInput(job.prompt);
    inputRef.current?.focus();
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDropOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    if (mode === 'create') {
      const next = event.relatedTarget;
      if (next instanceof Node && event.currentTarget.contains(next)) {
        return;
      }
    }
    setDropOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDropOver(false);
    const dropped = parseIntegrationDrop(event.dataTransfer);
    if (dropped) onDropRef(dropped);
  };

  const started = messages.some((message) => message.role === 'user');
  const showEmptyStage = mode === 'create' && !started;
  const sendDisabled = (!chatInput.trim() && !attachedRefs.length) || locked;
  const voiceActive =
    voiceState === 'recording' ||
    voiceState === 'transcribing' ||
    voiceState === 'speaking' ||
    Boolean(voiceError);

  const messageList = (
    <MessageList>
      {messages.map((m) => (
        <Bubble key={m.id} $role={m.role}>
          {m.content}
        </Bubble>
      ))}
      {showPending && (
        <PendingBubble role="status">Thinking…</PendingBubble>
      )}
      <div ref={endRef} />
    </MessageList>
  );

  const attachedRefsNode =
    attachedRefs.length > 0 ? (
      <AttachedRefs>
        {attachedRefs.map((ref) => (
          <AttachedRef key={`${ref.kind}:${ref.name}`}>
            {attachmentToken(ref)}
            <ChipRemove
              type="button"
              aria-label={`Remove ${ref.name}`}
              onClick={() =>
                setAttachedRefs((prev) =>
                  prev.filter(
                    (item) => item.kind !== ref.kind || item.name !== ref.name,
                  ),
                )
              }
            >
              ×
            </ChipRemove>
          </AttachedRef>
        ))}
      </AttachedRefs>
    ) : null;

  const micButton = (
    <VoiceMicButton
      recording={voiceState === 'recording'}
      testId="builder-voice-mic"
      onClick={voiceState === 'recording' ? stopRecording : startRecording}
      disabled={
        locked || voiceState === 'transcribing' || voiceState === 'speaking'
      }
    />
  );

  const voiceStatus = voiceActive ? (
    <VoiceChatBar
      voiceState={voiceState}
      error={voiceError}
      onStopSpeaking={stopSpeaking}
    />
  ) : null;

  return (
    <div>
      {mode === 'create' ? (
        <CreateShell
          $over={dropOver}
          data-testid="builder-dropzone"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {showEmptyStage ? (
            <Stage>
              <StageTitle>What should this agent do?</StageTitle>
              <StageLead>
                Start from a job, or write your own. I will propose a name,
                type, and model.
              </StageLead>
              <JobList>
                {CREATE_JOBS.map((job) => (
                  <JobRow
                    key={job.id}
                    type="button"
                    $selected={selectedJob === job.id}
                    aria-pressed={selectedJob === job.id}
                    onClick={() => chooseJob(job)}
                  >
                    <AgentMark $kind={job.mark} aria-hidden="true">
                      <JobTypeMark kind={job.mark} />
                    </AgentMark>
                    <JobCopy>
                      <JobTitle>{job.title}</JobTitle>
                      <JobDesc>{job.description}</JobDesc>
                    </JobCopy>
                  </JobRow>
                ))}
              </JobList>
            </Stage>
          ) : (
            messageList
          )}
          {(dropOver || attachedRefs.length > 0) && (
            <CreateDropSlot>
              {dropOver && (
                <DropHint>
                  Drop to attach this integration. Secrets stay on the
                  connection.
                </DropHint>
              )}
              {attachedRefsNode}
            </CreateDropSlot>
          )}
          <CreateComposer
            onSubmit={(event) => {
              event.preventDefault();
              void sendQuery(chatInput);
            }}
          >
            <CreateInput
              ref={inputRef}
              value={chatInput}
              onChange={(event) => setChatInput(event.target.value)}
              placeholder={placeholder}
              disabled={locked}
              rows={2}
              aria-label="Describe the agent"
            />
            <ComposerActions>
              {micButton}
              <SendButton
                type="submit"
                variant="primary"
                size="sm"
                disabled={sendDisabled}
              >
                Send
              </SendButton>
            </ComposerActions>
          </CreateComposer>
          {voiceStatus && <VoiceSlot>{voiceStatus}</VoiceSlot>}
        </CreateShell>
      ) : (
        <ChatShell>
          <ChatHeader>
            <HeaderRow>
              <div>
                <ChatTitle>Agent Builder</ChatTitle>
                <ChatSubtitle>{subtitle}</ChatSubtitle>
              </div>
            </HeaderRow>
          </ChatHeader>
          {messageList}
          <Dropzone
            $over={dropOver}
            data-testid="builder-dropzone"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <DropLabel $over={dropOver}>
              {dropOver ? 'Drop integration here' : 'Agent Builder dropzone'}
            </DropLabel>
            {attachedRefsNode}
            <ChatInputRow
              onSubmit={(event) => {
                event.preventDefault();
                void sendQuery(chatInput);
              }}
            >
              <ChatInput
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                placeholder={placeholder}
                disabled={locked}
                rows={1}
              />
              {micButton}
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={sendDisabled}
              >
                Send
              </Button>
            </ChatInputRow>
            {voiceStatus}
          </Dropzone>
        </ChatShell>
      )}

      {displayChips.length > 0 && (
        <ChipWrap>
          <QueryChips
            chips={displayChips.map((label) => ({ label }))}
            onSelect={handleChipSelect}
          />
        </ChipWrap>
      )}

      {pending && (
        <DiffCard>
          <strong>Proposed configuration change</strong>
          <div style={{ marginTop: '0.5rem' }}>
            {pending.agentName && <div>Name: {pending.agentName}</div>}
            {pending.agentType && <div>Type: {pending.agentType}</div>}
            {pending.model != null && pending.model !== currentConfig.model && (
              <div>
                Model: {currentConfig.model || '—'} → {pending.model}
              </div>
            )}
            {pending.temperature != null &&
              pending.temperature !== currentConfig.temperature && (
                <div>
                  Temperature: {currentConfig.temperature} →{' '}
                  {pending.temperature}
                </div>
              )}
            {pending.systemPrompt != null &&
              pending.systemPrompt !== currentConfig.systemPrompt && (
                <div>
                  System prompt updated
                  {pending.systemPrompt.includes('$DataSource:')
                    ? ' (includes $DataSource: directive)'
                    : ''}
                </div>
              )}
            {pending.recommendedModels && (
              <div>
                Recommended: {pending.recommendedModels.join(', ') || '—'}
              </div>
            )}
            {pending.dataSources && (
              <div>
                Data sources: {pending.dataSources.join(', ') || '—'}
              </div>
            )}
            {fleetDiffLines.length > 0 && (
              <div
                style={{
                  marginTop: '0.35rem',
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '0.75rem',
                }}
              >
                {fleetDiffLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
                <div>({pending.fleet?.length ?? 0} specialists)</div>
              </div>
            )}
          </div>
          <DiffActions>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleAccept()}
              disabled={saving || !editsEnabled}
            >
              Accept
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscard}
              disabled={saving}
            >
              Discard
            </Button>
          </DiffActions>
        </DiffCard>
      )}

      {actionError && <ErrorBanner>{actionError}</ErrorBanner>}
    </div>
  );
};

export default BuilderChat;
